import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import supabase from './utils/supabaseClient';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import GameLog from './components/GameLog';
import Navigation from './components/Navigation';
import Privacy from './components/Privacy';
import Tournaments from './components/Tournaments';
import CreateTournament from './components/CreateTournament';
import TournamentDetails from './components/TournamentDetails';
import { setupSafariPwaListeners, shouldSuggestAddToHomeScreen } from './utils/safariPwaHelpers';
import AddToHomeScreen from './components/AddToHomeScreen';
import { resetApp } from './serviceWorkerRegistration';

// Safe mode activation function
const enterSafeMode = () => {
  // Set safe mode flag in localStorage
  window.localStorage.setItem('kappasig_safe_mode', 'true');
  
  // Clear caches and reload
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      ).then(() => {
        console.log('All caches cleared for safe mode');
        // Force a clean reload
        window.location.href = window.location.origin;
      });
    });
  } else {
    window.location.href = window.location.origin;
  }
};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPwa, setIsPwa] = useState(false);
  const [showAddToHomeScreen, setShowAddToHomeScreen] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  // Check if app is in safe mode
  const [inSafeMode, setInSafeMode] = useState(
    window.localStorage.getItem('kappasig_safe_mode') === 'true'
  );
  // Flag to prevent duplicate profile creation attempts
  const [creatingProfile, setCreatingProfile] = useState(false);

  // Helper function to create a player profile
  const createPlayerProfile = async (userId, username) => {
    // Prevent concurrent profile creation attempts
    if (creatingProfile) {
      console.log('Profile creation already in progress, skipping');
      return;
    }
    
    try {
      setCreatingProfile(true);
      
      // First check if profile already exists
      const { data: existingProfile, error: profileCheckError } = await supabase
        .from('players')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (profileCheckError && profileCheckError.code !== 'PGRST116') {
        // PGRST116 means no rows returned, any other error is a real error
        console.error('Error checking for existing profile:', profileCheckError);
        return;
      }
      
      if (existingProfile) {
        console.log('Player profile already exists, not creating a new one');
        return;
      }
      
      // If username is not provided directly, try to fetch it from auth metadata
      if (!username) {
        const { data: userData } = await supabase.auth.getUser(userId);
        if (userData && userData.user) {
          // Try different possible locations where username might be stored
          username = userData.user.user_metadata?.username || 
                    userData.user.user_metadata?.data?.username || 
                    userData.user.user_metadata?.name ||
                    userData.user.email?.split('@')[0] ||
                    userId.substring(0, 8);
          console.log('Retrieved username from metadata:', username);
        }
      }
      
      console.log('Creating new player profile for', userId, 'with username:', username);
      
      // Create new player profile
      const { error } = await supabase
        .from('players')
        .insert([
          { 
            user_id: userId,
            username: username || userId.substring(0, 8), // Use username from metadata or fallback
            elo: 1000, // Starting ELO
            games_played: 0,
            games_won: 0,
            games_lost: 0,
            current_streak: 0,
            longest_streak: 0
          }
        ]);
        
      if (error) {
        console.error('Error creating player profile:', error.message);
      } else {
        console.log('Player profile created successfully');
      }
    } catch (error) {
      console.error('Error in profile creation process:', error.message);
    } finally {
      setCreatingProfile(false);
    }
  };

  useEffect(() => {
    // Set a loading timeout - if app is still loading after 10 seconds, force refresh
    const loadingTimeout = setTimeout(() => {
      if (loading) {
        console.log('App stuck in loading state - forcing refresh');
        window.location.reload();
      }
    }, 10000);
    
    // Show reset button after 5 seconds if still loading
    const resetButtonTimeout = setTimeout(() => {
      if (loading) {
        setShowResetButton(true);
      }
    }, 5000);
    
    // Check if running as PWA
    const checkPwaMode = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      const isMinimalUi = window.matchMedia('(display-mode: minimal-ui)').matches;
      const isIOSStandalone = window.navigator.standalone === true;
      
      if (isStandalone || isFullscreen || isMinimalUi || isIOSStandalone) {
        setIsPwa(true);
        
        // Update CSS variables for safe areas
        document.documentElement.style.setProperty('--safe-area-top', 'env(safe-area-inset-top, 0px)');
        document.documentElement.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom, 0px)');
        document.documentElement.style.setProperty('--safe-area-left', 'env(safe-area-inset-left, 0px)');
        document.documentElement.style.setProperty('--safe-area-right', 'env(safe-area-inset-right, 0px)');
      }
    };
    
    // Check for PWA mode initially and on display mode changes
    checkPwaMode();
    
    const displayModeQuery = window.matchMedia('(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)');
    const handleDisplayModeChange = () => {
      checkPwaMode();
    };
    
    displayModeQuery.addEventListener('change', handleDisplayModeChange);
    
    // Setup direct auth check without complex timeout handling
    const getInitialSession = async () => {
      try {
        // Add timeout for Supabase request
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Supabase connection timeout')), 10000) // Increase to 10 seconds
        );
        
        // Race the Supabase request against the timeout
        const { data } = await Promise.race([
          supabase.auth.getSession(),
          timeoutPromise
        ]);
        
        if (data && data.session) {
          setSession(data.session);
          
          // If session exists, ensure profile exists
          console.log('User metadata on initial session:', data.session.user.user_metadata);
          
          // Try to extract username from different possible locations in metadata
          const username = data.session.user.user_metadata?.username || 
                          data.session.user.user_metadata?.data?.username || 
                          data.session.user.user_metadata?.name;
                          
          await createPlayerProfile(data.session.user.id, username);
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
        // On timeout or error, continue without a session
        setSession(null);
      } finally {
        setLoading(false);
      }
    };
    
    getInitialSession();
    
    // Add auth listener with error handling
    let subscription = { unsubscribe: () => {} }; // Default empty subscription
    
    try {
      const result = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state changed:', event);
          setSession(session);
          
          // If user just signed in, check/create profile
          if (event === 'SIGNED_IN' && session) {
            // Log full user metadata to help debug
            console.log('User metadata on sign in:', session.user.user_metadata);
            
            // Try to extract username from different possible locations in metadata
            const username = session.user.user_metadata?.username || 
                           session.user.user_metadata?.data?.username || 
                           session.user.user_metadata?.name;
                           
            await createPlayerProfile(session.user.id, username);
          }
          
          setLoading(false);
        }
      );
      
      if (result && result.data && result.data.subscription) {
        subscription = result.data.subscription;
      } else {
        console.error('Error setting up auth listener: Invalid response structure');
      }
    } catch (error) {
      console.error('Error setting up auth listener:', error);
      setLoading(false);
    }
    
    // Setup Safari PWA specific handling
    setupSafariPwaListeners();
    
    // Check if we should suggest adding to home screen
    if (shouldSuggestAddToHomeScreen()) {
      setShowAddToHomeScreen(true);
    }
    
    return () => {
      // Clean up
      clearTimeout(loadingTimeout);
      clearTimeout(resetButtonTimeout);
      try {
        subscription.unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing from auth changes:', error);
      }
      displayModeQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, [loading]);

  // Handle logout function for Navigation component
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  // A simple retry handler if needed
  const handleRetry = () => {
    window.location.reload();
  };

  // Handle safe mode exit
  const exitSafeMode = () => {
    window.localStorage.removeItem('kappasig_safe_mode');
    window.location.href = window.location.origin;
  };

  // Force reset the entire app
  const forceReset = () => {
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    
    // Clear caches
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => caches.delete(cacheName));
      });
    }
    
    // Clear local storage
    localStorage.clear();
    
    // Force reload
    window.location.href = window.location.origin + '?reset=' + Date.now();
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="flex space-x-2 animate-pulse mb-8">
          <div className="w-3 h-3 bg-primary rounded-full"></div>
          <div className="w-3 h-3 bg-primary rounded-full animation-delay-200"></div>
          <div className="w-3 h-3 bg-primary rounded-full animation-delay-400"></div>
        </div>
        
        {showResetButton && (
          <div className="text-center">
            <p className="text-sm text-red-500 mb-2">Taking longer than expected to load</p>
            <button 
              onClick={forceReset}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
            >
              Force Reset
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Router>
      <div className="app-wrapper">
        <Navigation 
          user={session?.user ? {
            username: session.user.user_metadata?.username || session.user.email
          } : null} 
          onLogout={handleLogout} 
        />
        
        {inSafeMode && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
            <p className="font-bold">Safe Mode Active</p>
            <p>The app is running in safe mode with service workers disabled.</p>
            <button 
              onClick={exitSafeMode}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 mt-2 rounded"
            >
              Exit Safe Mode
            </button>
          </div>
        )}
        
        <main className="main-content">
          <Routes>
            <Route 
              path="/" 
              element={
                session ? (
                  <Dashboard session={session} supabase={supabase} />
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />
            <Route
              path="/login"
              element={
                !session ? (
                  <Login supabase={supabase} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/log-game"
              element={
                session ? (
                  <GameLog session={session} supabase={supabase} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route path="/privacy" element={<Privacy />} />
            
            {/* Tournament Routes */}
            <Route
              path="/tournaments"
              element={
                <Tournaments session={session} supabase={supabase} />
              }
            />
            <Route
              path="/tournament/create"
              element={
                session ? (
                  <CreateTournament session={session} supabase={supabase} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/tournament/:id"
              element={
                <TournamentDetails session={session} supabase={supabase} />
              }
            />
          </Routes>
        </main>
        
        <footer className="mt-auto py-6 border-t">
          <div className="container mx-auto">
            <div className="flex justify-center items-center text-sm">
              <a href="/privacy" className="hover:underline">Privacy Policy</a>
              <span className="text-muted-foreground mx-2">|</span>
              <span className="text-muted-foreground">KΣ Die Tracker © {new Date().getFullYear()}</span>
              <span className="text-muted-foreground mx-2">|</span>
              <button 
                onClick={enterSafeMode} 
                className="text-sm text-muted-foreground hover:text-primary hover:underline"
              >
                Safe Mode
              </button>
              <span className="text-muted-foreground mx-2">|</span>
              <button 
                onClick={() => resetApp()} 
                className="text-sm text-muted-foreground hover:text-primary hover:underline"
              >
                Reset App
              </button>
            </div>
          </div>
        </footer>
      </div>
      
      {/* Add to Home Screen Prompt */}
      {showAddToHomeScreen && (
        <AddToHomeScreen onClose={() => setShowAddToHomeScreen(false)} />
      )}
    </Router>
  );
}

export default App;
